/**
 * Session visit stack for Organizace / Obchody / Nakupovat browse.
 * Shell ← prefers this over named hierarchical parents when non-empty,
 * so Zpět walks “where I was” for as many steps as the cap allows.
 * Does not use browser history.back() — checkout/donation still own HierarchicalBackFlow.
 */
import { isReservedSalesPointSlug } from 'pi-kiosk-shared';

export interface BrowseVisitEntry {
  readonly pathname: string;
  readonly search: string;
}

export const BROWSE_VISIT_STACK_MAX = 50;

let stack: BrowseVisitEntry[] = [];
/** When true, the next recorder tick skips pushing (shell ← / consume already popped). */
let skipNextRecord = false;

function normalizePathname(pathname: string): string {
  const noHash = pathname.split('#', 1)[0] ?? pathname;
  const noQuery = noHash.split('?', 1)[0] ?? noHash;
  const trimmed = noQuery.trim();
  if (trimmed.length <= 1) {
    return trimmed === '' ? '/' : trimmed;
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

function normalizeSearch(search: string): string {
  if (search.length === 0 || search === '?') {
    return '';
  }
  return search.startsWith('?') ? search : `?${search}`;
}

export function browseVisitHref(entry: BrowseVisitEntry): string {
  return `${entry.pathname}${entry.search}`;
}

export function normalizeBrowseVisitEntry(
  pathname: string,
  search = '',
): BrowseVisitEntry {
  return {
    pathname: normalizePathname(pathname),
    search: normalizeSearch(search),
  };
}

export function browseVisitEntriesEqual(
  a: BrowseVisitEntry,
  b: BrowseVisitEntry,
): boolean {
  return a.pathname === b.pathname && a.search === b.search;
}

/**
 * Paths where visit-stack Zpět applies (Organizace, Obchody, Nakupovat + browse depth).
 * Checkout / donate / more / auth / recovery stay on hierarchical + HierarchicalBackFlow.
 */
export function isBrowseVisitStackablePath(pathname: string): boolean {
  const path = normalizePathname(pathname);

  if (path === '/orgs' || path.startsWith('/orgs/')) {
    return true;
  }
  if (path === '/browse' || path.startsWith('/browse/')) {
    return true;
  }
  if (path === '/shops' || path.startsWith('/shops/')) {
    return true;
  }
  if (path === '/favorites' || path.startsWith('/favorites/')) {
    return true;
  }
  if (path === '/sign-up' || path.startsWith('/sign-up/')) {
    // Platform org picker during registration — same Organizace surface.
    return true;
  }

  // /:tenant/... browse surfaces (tenant segment is first path part).
  const segments = path.split('/').filter((s) => s.length > 0);
  if (segments.length < 2) {
    return false;
  }
  const rest = `/${segments.slice(1).join('/')}`;

  if (rest === '/shops' || rest.startsWith('/shops/')) {
    return true;
  }
  if (rest === '/account/shop' || rest.startsWith('/account/shop/')) {
    return true;
  }
  // Catalog entry reached from Organizace / Nakupovat (not scanner depth — that has hierarchical).
  if (rest === '/shop' || rest === '/home') {
    return true;
  }
  if (rest.startsWith('/shop/') && !rest.startsWith('/shop/scanner')) {
    return true;
  }
  if (rest.startsWith('/home/')) {
    return true;
  }
  // Tenant kiosk slug catalog: /:tenant/:kioskSlug (single segment after tenant, not reserved).
  const slug = segments[1];
  if (
    segments.length === 2 &&
    slug !== undefined &&
    !isReservedSalesPointSlug(slug)
  ) {
    return true;
  }

  return false;
}

export function peekBrowseVisit(): BrowseVisitEntry | null {
  if (stack.length === 0) {
    return null;
  }
  return stack[stack.length - 1] ?? null;
}

export function browseVisitStackSize(): number {
  return stack.length;
}

export function pushBrowseVisit(entry: BrowseVisitEntry): void {
  const normalized = normalizeBrowseVisitEntry(entry.pathname, entry.search);
  const top = peekBrowseVisit();
  if (top !== null && browseVisitEntriesEqual(top, normalized)) {
    return;
  }
  stack = [...stack, normalized].slice(-BROWSE_VISIT_STACK_MAX);
}

export function popBrowseVisit(): BrowseVisitEntry | null {
  if (stack.length === 0) {
    return null;
  }
  const next = stack[stack.length - 1] ?? null;
  stack = stack.slice(0, -1);
  return next;
}

/**
 * Pop until a non-ephemeral browse entry (skip stale payment monitoring / QR / complete).
 * Returns null when the stack is exhausted.
 */
export function popSafeBrowseVisit(
  isUnsafe: (pathname: string) => boolean,
): BrowseVisitEntry | null {
  while (stack.length > 0) {
    const entry = popBrowseVisit();
    if (entry === null) {
      return null;
    }
    if (!isUnsafe(entry.pathname)) {
      return entry;
    }
  }
  return null;
}

/**
 * Peek the top safe entry, pruning ephemeral tops so Zpět enables correctly.
 */
export function peekSafeBrowseVisit(
  isUnsafe: (pathname: string) => boolean,
): BrowseVisitEntry | null {
  while (stack.length > 0) {
    const top = stack[stack.length - 1];
    if (top === undefined) {
      return null;
    }
    if (!isUnsafe(top.pathname)) {
      return top;
    }
    stack = stack.slice(0, -1);
  }
  return null;
}

/** Mark the next location change as a stack-driven back (do not re-push). */
export function markBrowseVisitBackNavigation(): void {
  skipNextRecord = true;
}

export function consumeBrowseVisitSkipRecord(): boolean {
  if (!skipNextRecord) {
    return false;
  }
  skipNextRecord = false;
  return true;
}

export function clearBrowseVisitStack(): void {
  stack = [];
  skipNextRecord = false;
}

/** Test helper — replace stack contents. */
export function __setBrowseVisitStackForTests(entries: readonly BrowseVisitEntry[]): void {
  stack = entries.map((e) => normalizeBrowseVisitEntry(e.pathname, e.search));
  skipNextRecord = false;
}
